import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface PillTabItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface PillTabsProps {
  tabs: PillTabItem[];
  activeId: string;
  onChange: (id: string) => void;
  size?: 'sm' | 'md';
  className?: string;
}

export function PillTabs({
  tabs,
  activeId,
  onChange,
  size = 'md',
  className,
}: PillTabsProps) {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-line bg-slate-100/80 p-1',
        'dark:border-slate-800 dark:bg-slate-800/80',
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full font-bold transition-all',
              size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-1.5 text-[13px]',
              isActive
                ? 'bg-brand text-white shadow-sm dark:bg-brand dark:text-white'
                : 'text-ink-2 hover:text-ink dark:text-slate-300 dark:hover:text-white',
              tab.disabled && 'pointer-events-none opacity-40',
            )}
          >
            {tab.icon && <span className="shrink-0" aria-hidden>{tab.icon}</span>}
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span
                className={cn(
                  'ms-1 rounded-full px-1.5 py-0.2 text-[10px] font-bold',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-slate-200 text-ink-3 dark:bg-slate-700 dark:text-slate-300',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default PillTabs;
